import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Facebook, Youtube, Phone, Mail, MapPin, Factory } from 'lucide-react';
import { useContent } from '../../lib/ContentContext';
import { assetUrl } from '../../lib/api';

const SOCIAL_ICONS = { Linkedin, Facebook, Youtube };

export const Footer = () => {
  const { content } = useContent();
  const f = content?.footer;
  if (!f) return null;

  return (
    <footer data-testid="site-footer" className="bg-[#26247B] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8">
        {/* Brand */}
        <div className="lg:col-span-3">
          {f.logo && (
            <img
              src={assetUrl(f.logo)}
              alt="Intexso Biochem"
              data-testid="site-footer-logo"
              className="w-auto object-contain block"
              style={{ height: '100px' }}
            />
          )}
          <h3 className="mt-3 font-extrabold text-lg">Intexso Biochem Pvt. Ltd.</h3>
          <p className="mt-4 text-white/85 text-sm leading-6">{f.description}</p>
        </div>

        {/* Social */}
        <div className="space-y-4 lg:col-span-3">
          <h4 className="font-extrabold text-sm tracking-wider uppercase">Social Connections:</h4>
          <div className="flex gap-3">
            {(f.social || []).map((s, i) => {
              const Icon = SOCIAL_ICONS[s.icon] || Linkedin;
              return (
                <a
                  key={i}
                  href={s.link}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="site-footer-social-link"
                  aria-label={s.icon}
                  className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition-colors duration-200"
                >
                  <Icon className="h-5 w-5" />
                </a>
              );
            })}
          </div>
        </div>

        {/* Quick links */}
        <div className="lg:col-span-2">
          <h4 className="font-extrabold text-sm tracking-wider uppercase mb-4">{f.quick_links_title}</h4>
          <ul className="space-y-2.5">
            {(f.quick_links || []).map((l, i) => (
              <li key={i}>
                <Link to={l.link} className="text-white/85 text-sm hover:text-white hover:underline transition-colors duration-200">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact info */}
        <div className="space-y-3.5 lg:col-span-4">
          <h4 className="font-extrabold text-sm tracking-wider uppercase">{f.contact_title}</h4>
          <div className="flex items-start gap-3">
            <Phone className="h-5 w-5 text-[#F4A300] mt-0.5 shrink-0" />
            <p className="font-bold text-sm">{f.phone}</p>
          </div>
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-[#F4A300] mt-0.5 shrink-0" />
            <p className="font-bold text-sm">{f.email}</p>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-[#F4A300] mt-0.5 shrink-0" />
            <p className="text-white/85 text-sm leading-6">{f.corporate_office}</p>
          </div>
          <div className="flex items-start gap-3">
            <Factory className="h-5 w-5 text-[#F4A300] mt-0.5 shrink-0" />
            <p className="text-white/85 text-sm leading-6">{f.factory}</p>
          </div>
        </div>
      </div>

      <div className="border-t border-white/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/75 text-xs">{f.copyright}</p>
          <div className="flex gap-6 text-xs text-white/75">
            <Link to="/privacy-policy" data-testid="site-footer-privacy-link" className="hover:text-white hover:underline">Privacy Policy</Link>
            <Link to="/terms-of-use" data-testid="site-footer-terms-link" className="hover:text-white hover:underline">Terms of Use</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
